import styled, { css } from 'styled-components';
import { focusVisibleMixin } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { Price, PriceComponent } from '../../../common';
import {
  Description,
  DescriptionComponent,
  Icons,
  IconsComponent,
  Image,
  ImageComponent,
  ImageComponentContainer,
  Name,
  NameComponent,
  StyledIconsItem
} from '../items-parts';
import type { SelectedItemLayout } from './ItemLayout';

export function ItemLayoutThree({
  data,
  width = 100,
  showImage = true,
  onClick,
  onKeyDown
}: SelectedItemLayout) {
  const {
    itemElements = [],
    showDefaultImage,
    itemLayout,
    sectionsLayout,
    popupEnabled
  } = useSettings();
  const { name, image, price, description, badges = [] } = data;

  const show =
    itemElements.includes('image') &&
    ((showImage &&
      (image || (itemLayout === 3 && sectionsLayout !== 'masonry'))) ||
      (!image && showDefaultImage));

  const interactiveProps = popupEnabled
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown
      }
    : {};

  return (
    <Component
      {...interactiveProps}
      style={{
        width: `calc(${width}% - 48px)`
      }}
      _showImage={!!show}
    >
      {show && <Image image={image} alt="" />}

      <Info>
        <NameContainer>
          <Name>{name}</Name>
        </NameContainer>

        {description && itemElements.includes('description') && (
          <Description>{description}</Description>
        )}

        {badges?.length > 0 && itemElements.includes('badges') && (
          <Icons icons={badges} />
        )}

        {price && itemElements.includes('price') && <Price price={price} />}
      </Info>
    </Component>
  );
}

const Component = styled.div<{ _showImage?: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
  margin: 16px 24px;

  &:hover,
  &:focus-visible {
    ${({ theme: { popupEnabled } }) =>
      popupEnabled &&
      css`
        cursor: pointer;

        ${NameComponent} {
          text-decoration: underline;
        }
      `}
  }

  ${focusVisibleMixin()}

  ${DescriptionComponent} {
    margin-top: 2px;
    text-align: center;
  }

  ${ImageComponentContainer} {
    max-width: 200px;
    max-height: 200px;
    width: 100%;
    height: 100%;
    margin-right: 0;

    ${({ _showImage }) =>
      _showImage &&
      css`
        margin-bottom: 12px;
      `}

    ${ImageComponent} {
      height: 200px;
    }
  }

  ${IconsComponent} {
    display: flex;
    justify-content: center;
    margin-top: 6px;
  }

  ${StyledIconsItem} {
    width: ${({
      theme: {
        itemNameFontStyle: { fontSize }
      }
    }) => fontSize * 1.5}px;
    height: ${({
      theme: {
        itemNameFontStyle: { fontSize }
      }
    }) => fontSize * 1.5}px;
  }

  ${PriceComponent} {
    text-align: center;
    margin-top: 8px;
  }
`;
const Info = styled.div``;
const NameContainer = styled.div``;
